import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, Optional, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfiguratorLoaderService } from '@icc/b2b/configurator';
import { DocumentManagerService } from '@icc/b2b/sales-document';
import {
    APP_VERSION,
    CountriesService,
    Delivery,
    SalesDocumentClient,
    SalesDocumentPosition,
    State,
    StaticPositionDetailsService,
} from '@icc/b2b/shared';
import { ConfigService, HOST } from '@icc/shared';
import { DetailsTreeNode } from '@icc/shared/helpers/b2b';
import { DialogService } from '@icc/ui';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ClientPreviewDialogComponent } from 'libs/b2b/sales-document/src/lib/client-preview-dialog/client-preview-dialog.component';
import { NgxPermissionsService } from 'ngx-permissions';
import { ReplaySubject, Subscription } from 'rxjs';
import SignaturePad from 'signature_pad';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'icc-delivery',
    templateUrl: './delivery.component.html',
    styleUrls: ['./delivery.component.scss'],
})
export class DeliveryComponent {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();

    openedDeliveryId: string | null = null;
    openedDelivery: Delivery | null = null;
    positions: SalesDocumentPosition[] = [];
    client: SalesDocumentClient | null = null;

    positionsMap: Map<string, SalesDocumentPosition> = new Map<string, SalesDocumentPosition>();
    positionsDetails = new Map<string, DetailsTreeNode>();
    positionsShowDetails = new Map<string, boolean>();
    positionsTitles = new Map<string, string[]>();
    positionsIndexes = new Map<string, string>();

    loadedConfiguratorCapabilities: string[] = [];
    loadingConfiguratorCapabilities = false;

    hasAccessTo = {
        signDelivery: false,
    };
    changedPositionSelection = false;
    deliveredPositions = new Set<string>();

    @ViewChild('signaturePad', { static: false })
    signaturePadElement: ElementRef<HTMLCanvasElement>;
    private signaturePad: SignaturePad;

    signMode = false;
    saving = false;

    hostname = this.host || window.location.origin;

    private subscriptions: Subscription[] = [];

    event = null;

    constructor(
        private route: ActivatedRoute,
        private configService: ConfigService,
        private router: Router,
        private http: HttpClient,
        private translateService: TranslateService,
        private staticPositionDetailsService: StaticPositionDetailsService,
        private configuratorLoaderService: ConfiguratorLoaderService,
        private permissionsService: NgxPermissionsService,
        private dialogService: DialogService,
        @Inject(APP_VERSION) private appVersion: string,
        private countriesService: CountriesService,
        @Optional() @Inject(HOST) private host: string
    ) {}

    ngOnInit(): void {
        this.countriesService.loadCountries();
        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                this.openedDeliveryId = params.id;
                this.loadDelivery();
            }),
        );
        this.checkAccess();
    }


    async checkAccess() {
        this.hasAccessTo.signDelivery = await this.permissionsService.hasPermission(
            'driver-deliveries'
        );
    }

    switchShowDetails(posId: SalesDocumentPosition['uuid'], showDetails: boolean) {
        this.positionsShowDetails.set(posId, showDetails);
    }

    async loadDelivery() {
        if (!this.openedDeliveryId) {
            return;
        }

        const { delivery, positions, client } = await this.http
            .post<{
                delivery: Delivery;
                positions: SalesDocumentPosition[];
                client: SalesDocumentClient;
            }>(`/api/transport/delivery/${this.openedDeliveryId}`, {
                userId: this.configService?.user?.user?.id,
                appVersion: this.appVersion,
            })
            .toPromise();
        this.openedDelivery = delivery;
        this.positions = positions;
        this.client = client;
        this.setDocumentCrumbs();

        const positionsMap: Map<string, SalesDocumentPosition> = new Map();
        positions.forEach((position) => {
            positionsMap.set(position.uuid, position);
        });
        this.positionsMap = positionsMap;

        for (const [positionId, status] of Object.entries(this.openedDelivery.positions)) {
            if (status === 'delivered') {
                this.deliveredPositions.add(positionId);
            } else {
                this.deliveredPositions.delete(positionId);
            }
            if (this.positionsMap.has(positionId)) {
                this.positionsMap.get(positionId).isDelivered = status === 'delivered';
            }
        }

        const positionsShowDetails = new Map<string, boolean>();
        const types = [];
        const configurations: Record<string, Record<string, any>> = {};
        for (const pos of positions) {
            positionsShowDetails.set(pos.uuid, this.positionsShowDetails.get(pos.uuid) ?? false);
            if (pos.type !== 'static' && pos.type !== 'group' && pos.type !== 'additional') {
                if (!types.includes(pos.type)) {
                    types.push(pos.type);
                    configurations[pos.type] = {};
                }
                configurations[pos.type][pos.uuid] = pos.configuration;
            }
        }
        this.positionsShowDetails = positionsShowDetails;

        if (types.length > 0) {
            await this.loadConfiguratorCapabilities(types, configurations);
        }
        await this.updatePositionDetails(positions);
    }

    async loadConfiguratorCapabilities(
        types: string[],
        configurations: Record<string, Record<string, any>>
    ) {
        if (!this.loadingConfiguratorCapabilities) {
            this.loadingConfiguratorCapabilities = true;
            const moduleRef = await this.configuratorLoaderService.loadConfigurator();
            const configuratorsRepositoryService =
                this.configuratorLoaderService.configuratorsRepositoryService;
            for (const type of types) {
                let configuratorIds = [0];
                if (type === 'complex') {
                    configuratorIds = Object.values(configurations[type]).map(
                        (conf) => conf.configuratorId
                    );
                }
                for (const configuratorId of configuratorIds) {
                    if (
                        !this.loadedConfiguratorCapabilities.includes(type + '-' + configuratorId)
                    ) {
                        await configuratorsRepositoryService.loadConfigurator(type, configuratorId);
                        this.loadedConfiguratorCapabilities.push(type + '-' + configuratorId);
                    }
                }
            }

            this.loadingConfiguratorCapabilities = false;
        }
    }

    async updatePositionDetails(positions: SalesDocumentPosition[]) {
        for (const position of positions) {
            if (position.title?.length) {
                this.positionsTitles.set(position.uuid, position.title);
            }
            if (this.staticPositionDetailsService.isStaticPosition(position)) {
                const tree = this.staticPositionDetailsService.getDetailsTree(
                    position,
                    this.translateService.instant.bind(this.translateService)
                );
                this.positionsDetails.set(position.uuid, tree);
                if (!position.title?.length) {
                    const title = this.staticPositionDetailsService.getPositionTitle(
                        position,
                        this.translateService.instant.bind(this.translateService)
                    );
                    this.positionsTitles.set(position.uuid, title);
                }
            } else {
                if (this.configuratorLoaderService.configuratorsRepositoryServiceStatic) {
                    const configuratorId =
                        position.type === 'complex' ? position.configuration.configuratorId : 0;
                    await this.configuratorLoaderService.configuratorsRepositoryService.loadConfigurator(
                        position.type,
                        configuratorId
                    );
                    const getDetailsTree =
                        this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getDetailsTree(
                            position.type,
                            configuratorId
                        );
                    if (getDetailsTree) {
                        const tree = getDetailsTree(
                            position.configuration,
                            this.translateService.instant.bind(this.translateService)
                        );
                        this.positionsDetails.set(position.uuid, tree);
                    }
                    if (!position.title?.length) {
                        const getPositionTitle =
                            this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getPositionTitle(
                                position.type,
                                configuratorId
                            );
                        if (getPositionTitle) {
                            const title = getPositionTitle(
                                position.configuration,
                                this.translateService.instant.bind(this.translateService)
                            );
                            this.positionsTitles.set(position.uuid, title);
                        }
                    }
                }
            }
        }
    }

    isEmpty(value: any) {
        if (value === null || value === undefined) {
            return true;
        }
        if (typeof value === 'string' && value.trim() === '') {
            return true;
        }
        if (Array.isArray(value) && value.length === 0) {
            return true;
        }
        if (typeof value === 'object' && Object.keys(value).length === 0) {
            return true;
        }
        if (typeof value === 'boolean' && value === false) {
            return true;
        }
        return false;
    }

    async openClientPreviewDialog() {
        this.dialogService
            .open(ClientPreviewDialogComponent, {
                data: {
                    client: this.client,
                    isSeller: false,
                },
            })
            .afterClosed();
    }

    getRegion(countryId: string, regionId: number) {
        return this.countriesService
            .getRegions(countryId)
            .find((region) => Number(region.id) === Number(regionId));
    }

    getRegionName(countryId: string, regionId: number) {
        const region = this.getRegion(countryId, regionId);
        if (region) {
            return region.name;
        }
        return '';
    }

    getCountry(countryId: string) {
        return this.countriesService.getCountry(countryId);
    }

    async changeDriverNote(note: string) {
        this.openedDelivery.driverNote = note;

        await this.http
            .post(`/api/transport/delivery/${this.openedDeliveryId}/note`, {
                driverNote: note,
                userId: this.configService?.user?.user?.id,
                appVersion: this.appVersion,
            })
            .toPromise();
    }

    updatePositionDeliveryStatus(position: SalesDocumentPosition, delivered: boolean) {
        this.openedDelivery.positions[position.uuid] = delivered ? 'delivered' : 'sent';
        this.changedPositionSelection = Object.values(this.openedDelivery.positions).some(
            (status) => status === 'delivered'
        );

        if (delivered) {
            this.deliveredPositions.add(position.uuid);
        } else {
            this.deliveredPositions.delete(position.uuid);
        }
    }

    async signDelivery() {
        this.signMode = true;
        document.body.classList.add('cdk-global-scrollblock');
        if (Capacitor.isNativePlatform()) {
            this.event = (event) => {
                if (this.signMode) {
                    this.initPad();
                }
            };
            screen.orientation.addEventListener("change", this.event);
            await ScreenOrientation.lock({ orientation: 'landscape' });
        } else {
            setTimeout(() => {
                this.initPad();
            }, 0);
        }
    }

    private initPad() {
        const canvas = this.signaturePadElement?.nativeElement;
        if (canvas) {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        }
        if (this.signMode && this.signaturePadElement && !this.signaturePad) {
            this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement);
            this.signaturePad.clear();
            if (this.openedDelivery?.signatureImage) {
                this.signaturePad.fromDataURL(this.openedDelivery.signatureImage, { ratio: 1 });
                this.signaturePad.off();
            }
        }
    }


    async cancelSign() {
        this.signMode = false;
        document.body.classList.remove('cdk-global-scrollblock');
        if (Capacitor.isNativePlatform()) {
            await ScreenOrientation.lock({ orientation: 'portrait' });
            await ScreenOrientation.removeAllListeners();
        }
        screen.orientation.removeEventListener("change", this.event);
        if (this.signaturePad) {
            this.signaturePad.off();
            this.signaturePad = null;
        }
    }

    clearSign() {
        if (this.signaturePad) {
            this.signaturePad.clear();
        }
    }

    async saveSign() {
        this.saving = true;
        const signature = this.signaturePad.toDataURL();
        this.openedDelivery.signatureImage = signature;
        this.openedDelivery.isConfirmed = true;

        try {
            await this.http
                .post(`/api/transport/delivery/${this.openedDeliveryId}/sign`, {
                    signatureImage: signature,
                    positions: this.openedDelivery.positions,
                    userId: this.configService?.user?.user?.id,
                    appVersion: this.appVersion,
                })
                .toPromise();

            this.signMode = false;
            if (this.signaturePad) {
                this.signaturePad.off();
                this.signaturePad = null;
            }
            document.body.classList.remove('cdk-global-scrollblock');
            this.saving = false;
            if (Capacitor.isNativePlatform()) {
                await ScreenOrientation.removeAllListeners();
                await ScreenOrientation.lock({ orientation: 'portrait' });
            }

            this.router.navigate(['transport', 'deliveries']);
        } catch (e) {
            console.error(e);
        }
    }

    async ngOnDestroy() {
        if (this.signaturePad) {
            this.signaturePad.off();
            this.signaturePad = null;
        }
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        document.body.classList.remove('cdk-global-scrollblock');
        if (Capacitor.isNativePlatform()) {
            await ScreenOrientation.lock({ orientation: 'portrait' });
            await ScreenOrientation.removeAllListeners();
        }
    }

    setDocumentCrumbs() {
        DeliveryComponent.crumbsSubject.next({
            level: 1,
            crumbs: [
                {
                    label: this.translateService.instant('DELIVERY|Lista dostaw'),
                    url: '/transport/deliveries',
                },
            ],
        });

        DeliveryComponent.crumbsSubject.next({
            level: 2,
            crumbs: [
                {
                    label: this.translateService.instant('DELIVERY|Dostawa'),
                    url: '/transport/delivery/' + this.openedDeliveryId,
                },
            ],
        });
    }
}
